import { useEffect, useState } from 'react';
import { Loader2, AlertCircle } from 'lucide-react';
import Badge from '../../ui/Badge';
import VendorRecoRow from './VendorRecoRow';
import { getPoAuditDetail } from '../../../api/mec';
import { formatMoney } from '../../../utils/formatters';
import { formatDelta, countIssues } from './poAuditHelpers';

// Accordion panel rendered inline under a PO row in PoAuditSummaryTable.
// Fetches per-PO detail (vendors + their qbo_bills + tek_pops) and lets
// each vendor row expand individually.

export default function PoAuditAccordion({ closeRunId, poNumber, domain }) {
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [expandedVendor, setExpandedVendor] = useState(null);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    setError(null);
    setDetail(null);
    setExpandedVendor(null);
    getPoAuditDetail(closeRunId, poNumber, domain)
      .then((data) => alive && setDetail(data))
      .catch((err) => alive && setError(err?.message || 'Failed to load PO detail'))
      .finally(() => alive && setLoading(false));
    return () => { alive = false; };
  }, [closeRunId, poNumber, domain]);

  if (loading) {
    return (
      <div className="px-5 py-4 bg-[var(--color-cream-100)]/40 border-y border-[var(--color-warm-100)]">
        <div className="flex items-center gap-2 text-[12px] text-[var(--color-warm-500)]">
          <Loader2 size={13} className="animate-spin" />
          Loading vendor breakdown for PO {poNumber}…
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="px-5 py-4 bg-[var(--color-danger-50)]/40 border-y border-[var(--color-warm-100)]">
        <div className="flex items-center gap-2 text-[12px] text-[var(--color-danger-700)]">
          <AlertCircle size={13} />
          {error}
        </div>
      </div>
    );
  }

  if (!detail) return null;

  const ss = detail.status_summary || {};
  const issues = countIssues(ss);
  const vendors = detail.vendors || [];

  return (
    <div className="bg-[var(--color-cream-100)]/40 border-y border-[var(--color-warm-100)]">
      {/* Header strip */}
      <div className="px-5 py-3 flex items-center gap-3 flex-wrap text-[12px] border-b border-[var(--color-warm-100)]/60">
        <span className="text-[13px] font-semibold text-[var(--color-warm-900)] mono-nums">
          PO {detail.po_number}
        </span>
        <span className="text-[var(--color-warm-300)]">·</span>
        <span className="text-[var(--color-warm-600)]">
          <span className="font-semibold text-[var(--color-warm-800)]">{ss.vendors_total || vendors.length}</span> vendors
        </span>
        <span className="text-[var(--color-warm-300)]">·</span>
        <span className="text-[var(--color-warm-600)]">
          <Badge tone={issues > 0 ? 'action' : 'recovery'} size="xs">
            {issues} {issues === 1 ? 'issue' : 'issues'}
          </Badge>
        </span>
        <span className="text-[var(--color-warm-300)]">·</span>
        <span className="text-[var(--color-warm-600)]">
          QBO <span className="tabular-nums font-semibold text-[var(--color-warm-800)]">{formatMoney((detail.qbo_total_cents || 0) / 100)}</span>
        </span>
        <span className="text-[var(--color-warm-300)]">·</span>
        <span className="text-[var(--color-warm-600)]">
          Tek <span className="tabular-nums font-semibold text-[var(--color-warm-800)]">{formatMoney((detail.tek_loaded_cents || 0) / 100)}</span>
        </span>
        <span className="text-[var(--color-warm-300)]">·</span>
        <span className={`tabular-nums font-semibold ${
          (detail.delta_cents || 0) === 0
            ? 'text-[var(--color-warm-600)]'
            : (detail.delta_cents || 0) > 0
              ? 'text-[var(--color-danger-700)]'
              : 'text-[var(--color-action-700)]'
        }`}>
          Δ {formatDelta(detail.delta_cents || 0)}
        </span>
        {detail.ro_numbers?.length > 0 && (
          <>
            <span className="text-[var(--color-warm-300)]">·</span>
            <span className="text-[var(--color-warm-500)]">
              RO {detail.ro_numbers.join(', ')}
            </span>
          </>
        )}
      </div>

      {/* Vendor list */}
      {vendors.length === 0 ? (
        <div className="px-5 py-6 text-center text-[12px] italic text-[var(--color-warm-500)]">
          No vendor reconciliation rows for this PO.
        </div>
      ) : (
        <div>
          {vendors.map((v) => {
            const key = v.vendor_canonical || v.vendor_display;
            return (
              <VendorRecoRow
                key={key}
                vendor={v}
                expanded={expandedVendor === key}
                onToggleExpand={() => setExpandedVendor((cur) => (cur === key ? null : key))}
              />
            );
          })}
        </div>
      )}
    </div>
  );
}
